import { create } from "zustand";

/**
 * toastStore — global toast / notification queue.
 *
 * Why a store and not a Context provider:
 *   Toasts can be raised from anywhere — services, fetch interceptors,
 *   route guards.  Putting the store at module scope means we don't
 *   need to thread a provider down or pass refs around.
 *
 * API (use from any component or service):
 *   import { toast } from "../stores/toastStore";
 *   toast.success("Logged in");
 *   toast.error("Network error");
 *   toast.info("Calculation queued");
 *
 * Each toast auto-dismisses after `duration` ms (default 4000) but the
 * user can also dismiss manually by clicking it (handled in <Toaster />).
 *
 * Anti-flicker: when a duplicate (same type + message) arrives within 800ms
 * of the previous one, we extend the existing toast's lifetime instead of
 * stacking a copy.
 */

const DEFAULT_DURATION_MS = 4000;
const DEDUPE_WINDOW_MS    = 800;

let nextId = 1;

export const useToastStore = create((set, get) => ({
  toasts: [],

  push: ({ type = "info", message, duration = DEFAULT_DURATION_MS }) => {
    const now = Date.now();
    const existing = get().toasts;

    // De-duplicate: same type + message arriving within the dedupe window
    // resets the lifetime instead of stacking.
    const dupe = existing.find(
      (t) => t.type === type && t.message === message && now - t.createdAt < DEDUPE_WINDOW_MS,
    );
    if (dupe) {
      set({
        toasts: existing.map((t) =>
          t.id === dupe.id ? { ...t, expiresAt: now + duration } : t,
        ),
      });
      return dupe.id;
    }

    const id = nextId++;
    const toast = {
      id,
      type,
      message,
      createdAt: now,
      expiresAt: now + duration,
    };
    set({ toasts: [...existing, toast] });

    // Schedule auto-dismiss.  Clearing on manual dismiss is handled in <Toaster />.
    setTimeout(() => {
      const t = get().toasts.find((x) => x.id === id);
      // If the user extended the toast (re-pushed), respect the new expiry.
      if (t && Date.now() >= t.expiresAt) {
        set({ toasts: get().toasts.filter((x) => x.id !== id) });
      }
    }, duration + 50);

    return id;
  },

  dismiss: (id) => {
    set({ toasts: get().toasts.filter((t) => t.id !== id) });
  },

  clear: () => set({ toasts: [] }),
}));

// Imperative façade — what 99% of callers should use.
export const toast = {
  success: (message, options = {}) => useToastStore.getState().push({ ...options, type: "success", message }),
  error:   (message, options = {}) => useToastStore.getState().push({ ...options, type: "error",   message }),
  info:    (message, options = {}) => useToastStore.getState().push({ ...options, type: "info",    message }),
};
